import { ActionBar, Alert, Button, Dialog, DialogClose } from '@pms-digital/design-system';
import { type ReactNode, useRef } from 'react';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  /** What the confirm button reads while the call runs (« Archivage… »). */
  pendingLabel: string;
  /**
   * Material Symbols name of the confirm button's icon (« archive »). Any
   * other node is ignored: the package `Button` only takes an icon name, and
   * its destructive variant always carries « warning ».
   */
  confirmIcon: ReactNode;
  /** `default` (plein bleu nuit) for a reversible action, `destructive` for an irreversible one. */
  confirmVariant: 'default' | 'destructive';
  isPending: boolean;
  /** Shown under the text when the call failed; the dialog stays open and the button retries. */
  errorMessage: string | null;
  onConfirm: () => void;
  /** « Annuler », Escape and the close cross — all refused while the call runs. */
  onCancel: () => void;
}

/**
 * A confirmation before an action on a whole company (issue #20, planches 02
 * and 04): the pattern of the matrix deletion dialog. The focus lands on
 * « Annuler » so that Enter never confirms by accident; while the call runs
 * both buttons are disabled and the dialog cannot be dismissed.
 */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  pendingLabel,
  confirmIcon,
  confirmVariant,
  isPending,
  errorMessage,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const destructive = confirmVariant === 'destructive';

  function handleOpenChange(next: boolean) {
    if (next || isPending) return;
    onCancel();
  }

  return (
    <Dialog
      open={open}
      onOpenChange={handleOpenChange}
      title={title}
      description={description}
      tone={destructive ? 'destructive' : 'default'}
      initialFocus={cancelRef}
      actions={
        <ActionBar
          align={destructive ? 'center' : 'end'}
          secondary={
            <DialogClose asChild>
              <Button ref={cancelRef} type="button" variant="ghost" disabled={isPending}>
                Annuler
              </Button>
            </DialogClose>
          }
          primary={
            <Button
              type="button"
              variant={destructive ? 'destructive' : 'secondary'}
              {...(typeof confirmIcon === 'string' ? { icon: confirmIcon } : {})}
              loading={isPending}
              loadingLabel={pendingLabel}
              disabled={isPending}
              onClick={onConfirm}
            >
              {confirmLabel}
            </Button>
          }
        />
      }
    >
      {errorMessage && <Alert tone="danger">{errorMessage}</Alert>}
    </Dialog>
  );
}
